import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import { Tooltip as ReactTooltip } from "react-tooltip";
import { Row, Col } from "react-bootstrap";
import Toggle from "react-toggle";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import moment from "moment";
import {
  endOfDay,
  endOfMonth,
  startOfMonth,
  startOfDay,
  addMonths,
  addDays,
  isSameDay,
  isLastDayOfMonth
} from "date-fns";
import { Input } from "formsy-react-components";
import { List, AutoSizer } from "react-virtualized";

import DatePicker from "../common/DatePicker";

const generateSegments = (startDate, endDate, budgetHours) => {
  const segments = [];
  let currentDate = startDate;

  // First segment: from startDate to end of that month
  segments.push({
    startDate: startOfDay(currentDate),
    endDate: endOfDay(endOfMonth(currentDate)),
    budgetHours
  });

  currentDate = startOfMonth(addMonths(currentDate, 1));

  while (currentDate < endDate) {
    const nextSegmentStartDate = currentDate;
    const nextSegmentEndDate = endOfMonth(nextSegmentStartDate);

    const segment = {
      startDate: startOfDay(nextSegmentStartDate),
      endDate: endOfDay(nextSegmentEndDate),
      budgetHours
    };

    segments.push(segment);

    currentDate = startOfMonth(addMonths(currentDate, 1));
  }

  // Ensure last segment ends exactly on project end date
  if (segments.length > 0) {
    segments[segments.length - 1].endDate = endOfDay(endDate);
  }

  return segments;
};

const MonthlySegmentForm = ({
  data,
  startDate,
  endDate,
  onSegmentsChange,
  dateHasChanged,
  isNightMode
}) => {
  const [segments, setSegments] = useState([]);

  const disabled = useMemo(
    () =>
      !startDate ||
      !endDate ||
      !moment(startDate).isBefore(endDate) ||
      !moment(endDate).isAfter(startDate),
    [startDate, endDate]
  );

  const handleSegmentsChange = (index, hours) => {
    const newSgments = [...segments];
    console.log(hours, "number hours");
    newSgments[index].budgetHours = hours;
    setSegments(newSgments);
    onSegmentsChange(newSgments, true);
  };

  const handleToggle = (e) => {
    if (e.target.checked) {
      if (data.length) {
        setSegments(data);
      } else {
        setSegments(
          generateSegments(new Date(startDate), new Date(endDate), 0)
        );
      }
    } else {
      setSegments([]);
    }
    onSegmentsChange(null, e.target.checked);
  };

  useEffect(() => {
    if (data.length) {
      setSegments(data);
    }
  }, [data]);

  useEffect(() => {
    if (segments.length && dateHasChanged) {
      if (!isSameDay(new Date(startDate), new Date(segments[0].startDate))) {
        const newSegments = generateSegments(
          new Date(startDate),
          new Date(endDate),
          0
        );
        setSegments(newSegments);
        onSegmentsChange(newSegments, true);
      } else if (
        !isSameDay(
          new Date(endDate),
          new Date(segments[segments.length - 1].endDate)
        )
      ) {
        const currentSegments = [...segments];
        if (
          new Date(endDate) > new Date(segments[segments.length - 1].endDate)
        ) {
          // Add new objects if new end date is after the last object's end date

          const lastSegmentEndDate = new Date(
            segments[segments.length - 1].endDate
          );

          // If the last segment ended in the last day of the month
          // the new segment should start on the first day of the next month
          // otherwise we need to recalculate the last segment
          const newSegmentStartDate = isLastDayOfMonth(lastSegmentEndDate)
            ? addDays(lastSegmentEndDate, 1)
            : startOfMonth(lastSegmentEndDate);

          const newSegments = generateSegments(newSegmentStartDate, endDate, 0);

          if (!isLastDayOfMonth(lastSegmentEndDate)) {
            const oldLastSegment = currentSegments.pop();
            newSegments[newSegments.length - 1]._id = oldLastSegment._id;
          }

          currentSegments.push(...newSegments);
          setSegments(currentSegments);
          onSegmentsChange(currentSegments, true);
        } else {
          // Update or remove objects if new end date is before the last object's end date

          const newSegments = currentSegments?.filter(
            (obj) => new Date(obj?.startDate) <= new Date(endDate)
          );

          if (newSegments?.length > 0 && new Date(endDate)) {
            newSegments[newSegments.length - 1].endDate = new Date(endDate);
            setSegments(newSegments);
            onSegmentsChange(newSegments, true);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, startDate, segments, dateHasChanged]);

  const rowRenderer = ({ key, index, style }) => (
    <div key={key} style={style} className="list-item">
      <Col md={4}>
        <DatePicker
          type="date"
          name="startDate"
          label="Start Date"
          disabled
          value={segments[index].startDate}
        />
      </Col>
      <Col md={4}>
        <DatePicker
          type="date"
          name="endDate"
          label="End Date"
          disabled
          value={segments[index].endDate}
        />
      </Col>
      <Col md={4}>
        <Input
          required
          type="number"
          name="budgetHours"
          label="Budget Hours"
          onChange={(_, value) => {
            handleSegmentsChange(index, Number(value));
          }}
          value={segments[index].budgetHours}
          step="0.5"
          pattern="[0-9]*"
        />
      </Col>
    </div>
  );

  return (
    <div style={{ marginTop: 0, marginLeft: 0, marginBottom: 0 }}>
      <fieldset>
        <legend className="flex -space-between" style={{ margin: 0 }}>
          Monthly Segments
          <span
            id="budget-segment-toggle"
            data-tooltip-variant={isNightMode ? "dark" : "light"}
            style={{
              margin: 0,
              justifySelf: "flex-end",
              height: "20px"
            }}
          >
            <Toggle
              name="collapsed"
              onChange={handleToggle}
              checked={!!segments.length}
              icons={{
                checked: <IoCheckmarkCircle />,
                unchecked: <IoCloseCircle />
              }}
              disabled={disabled}
            />
            <ReactTooltip
              place="left"
              anchorId="budget-segment-toggle"
              className={`custom-tooltip ${isNightMode ? "dark" : "light"}`}
            >
              Use Monthly Segments
            </ReactTooltip>
          </span>
        </legend>
      </fieldset>
      <Row
        style={{
          minHeight: 161,
          maxHeight: 500,
          overflowY: "auto",
          border: "1px solid rgb(80, 172, 203)",
          borderRadius: "0 0 4px 4px",
          margin: 0,
          paddingTop: "0.5rem"
        }}
      >
        {!!segments.length && (
          <div style={{ height: 224, overflow: "hidden" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  width={width}
                  height={height}
                  rowCount={segments.length}
                  rowHeight={80}
                  rowRenderer={rowRenderer}
                />
              )}
            </AutoSizer>
          </div>
        )}
        {!segments.length && (
          <h3 style={{ textAlign: "center", marginTop: 50 }}>
            No Monthly Segments
          </h3>
        )}
      </Row>
    </div>
  );
};

MonthlySegmentForm.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  data: PropTypes.array,
  onSegmentsChange: PropTypes.func,
  dateHasChanged: PropTypes.bool,
  isNightMode: PropTypes.bool
};

MonthlySegmentForm.defaultProps = {
  data: []
};

export default MonthlySegmentForm;
